import { Dialog, useMediaQuery, useTheme } from '@mui/material';
import SignupContentWrapper from '../../SignupContentWrapper';
import { DesignTokens, GivelifyButton, GivelifyLabel } from '@givelify/ui';
import locationIcon from '../../../../../assets/location-icon.svg';
import phoneIcon from '../../../../../assets/phone-icon.svg';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import {
    ButtonWrapper,
    Card,
    Divider,
    InfoContainer,
    Left,
    BannerImg,
    PastorImg,
    Right,
    StyledButton,
} from './styles';
import { webConfig } from 'webConfig';
import avatarDefault from './avatar-default.svg';
import coverDefault from './cover-default.svg';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import {
    CLICK_ALREADY_REGISTERED_GIVE_HERE,
    CLICK_ALREADY_REGISTERED_SIGN_IN,
    CLICK_EVENT,
    VIEW_EVENT,
    VIEW_ORG_ALREADY_REGISTERED_PAGE,
} from 'utils/clevertapEvents';
import { Features } from 'store/featureFlags/types';
import { pushEvent } from 'utils/clevertapUtils';
import { phoneFormatter } from '@givelify/givelify-ui';
import { getGPImageUrl } from 'pages/signup/utils/getGPImageUrl';
import { SEARCH_EXPERIMENT, SEARCH_UI_EXPERIMENT } from 'utils/clevertapVariantFlags';
import { useFullStoryIdentity } from 'pages/signup/utils/useFullStoryIdentity';

type AlreadyRegisteredProps = {
    doneeName: string;
    official: string;
    address1: string;
    address2: string;
    phone: string;
    pastorImg: string;
    photo: string;
    bgaLink: string;
    onBackClick: () => void;
};

const AlreadyRegistered: React.FC<AlreadyRegisteredProps> = ({
    doneeName,
    official,
    address1,
    address2,
    phone,
    pastorImg,
    photo,
    bgaLink,
    onBackClick,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const copy = useMemo(
        () => ({
            heading: t('signup.alreadyRegistered.heading'),
            manage: t('signup.alreadyRegistered.manage'),
            signIn: t('signup.alreadyRegistered.signIn'),
            donate: t('signup.alreadyRegistered.donate'),
            giveHere: t('signup.alreadyRegistered.giveHere'),
        }),
        [t],
    );
    const { fingerprint, fullStoryEnabled, newSearchUIEnabled, v2SearchAPIEnabled } = useSelector(
        (state: AppState) => ({
            fingerprint: state.Signup.fingerprint,
            fullStoryEnabled: state.FeatureFlagging.features[Features.FULLSTORY],
            newSearchUIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_UI_V2],
            v2SearchAPIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_API_V2],
        }),
    );

    useEffect(() => {
        pushEvent(VIEW_ORG_ALREADY_REGISTERED_PAGE, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off',
        });
        pushEvent(VIEW_EVENT, {
            name: "SignupReturningOrg",
            doneeName: doneeName,
        });
    }, [doneeName, newSearchUIEnabled, v2SearchAPIEnabled]);
    useFullStoryIdentity({
        enabled: fullStoryEnabled,
        fingerprint,
    });

    return (
        <SignupContentWrapper
            showCloseButton={true}
            heading={copy.heading}
            disableAnimation
            onBackClick={onBackClick}
        >
            <Card>
                <Left>
                    <BannerImg
                        src={getGPImageUrl(photo, coverDefault)}
                        alt="banner"
                        onError={(event) => {
                            event.currentTarget.src = coverDefault;
                        }}
                    />
                    <PastorImg
                        src={getGPImageUrl(pastorImg, avatarDefault)}
                        onError={(event) => {
                            event.currentTarget.src = avatarDefault;
                        }}
                    />
                    <InfoContainer className="fs-unmask">
                        <GivelifyLabel
                            variant={isMobile ? 'heading3S' : 'heading2S'}
                            text={doneeName}
                        />
                        <GivelifyLabel
                            variant={isMobile ? 'body1' : 'heading3S'}
                            text={official}
                            color={DesignTokens.color.textSecondary}
                        />
                        <div className="contact-section">
                            <img src={locationIcon} alt="locationIcon" />
                            <div className="address">
                                <GivelifyLabel
                                    variant="body2"
                                    text={address1}
                                />
                                <GivelifyLabel
                                    variant="body2"
                                    text={address2}
                                />
                            </div>
                            <img src={phoneIcon} alt="phoneIcon" />
                            <GivelifyLabel
                                variant="body2"
                                text={phoneFormatter(phone)}
                            />
                        </div>
                    </InfoContainer>
                </Left>
                <Right>
                    <ButtonWrapper className="fs-unmask">
                        <GivelifyLabel
                            variant={isMobile ? 'body1' : 'heading3S'}
                            text={copy.manage}
                        />
                        <GivelifyButton
                            text={copy.signIn}
                            variant="primary"
                            onClick={() => {
                                window.location.href =
                                    webConfig.givelifySignInPageUrl;
                                pushEvent(CLICK_ALREADY_REGISTERED_SIGN_IN, {
                                    [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
                                    [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled
                                        ? 'on'
                                        : 'off',
                                });
                                pushEvent(CLICK_EVENT, {
                                    name: "ManageOrg",
                                    location: "SignupReturningOrg",
                                });
                            }}
                        />
                    </ButtonWrapper>
                    <Divider />
                    <ButtonWrapper className="fs-unmask">
                        <GivelifyLabel
                            variant={isMobile ? 'body1' : 'heading3S'}
                            text={copy.donate}
                        />
                        <StyledButton
                            text={copy.giveHere}
                            variant="secondary"
                            onClick={() => {
                                window.open(bgaLink, '_blank');
                                pushEvent(CLICK_ALREADY_REGISTERED_GIVE_HERE, {
                                    [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
                                    [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled
                                        ? 'on'
                                        : 'off',
                                });
                                pushEvent(CLICK_EVENT, {
                                    name: "GiveHere",
                                    location: "SignupReturningOrg",
                                });
                            }}
                        />
                    </ButtonWrapper>
                </Right>
            </Card>
        </SignupContentWrapper>
    );
};

const Wrapper: React.FC<{ onBackClick: () => void }> = ({ onBackClick }) => {
    const { selectedOrganization } = useSelector((state: AppState) => ({
        selectedOrganization: state.Signup.selectedOrganization,
    }));

    if (!selectedOrganization?.source) return null;

    const {
        name,
        address,
        official,
        phone,
        pastorAvatar,
        photo,
        bgaLink,
        city,
        state,
        zip,
        country,
    } = selectedOrganization?.source;

    const countryName = country?.toLocaleLowerCase() === 'bah' ? 'BAH' : 'USA';

    return (
        <Dialog fullScreen open TransitionProps={{ timeout: 0 }}>
            <AlreadyRegistered
                doneeName={name}
                address1={address}
                address2={`${city} ${state} ${zip}, ${countryName}`}
                official={official}
                phone={phone}
                pastorImg={pastorAvatar}
                photo={photo}
                bgaLink={bgaLink}
                onBackClick={onBackClick}
            />
        </Dialog>
    );
};

export default Wrapper;
